// Left chrome (HUB-275) — logo, pinned entities/views, Workspace anchors, user
// foot. Ported from the agreed mockup; collapsible. Pins are a placeholder until
// the personalization slice (pins = relationships from the user's Home entity).

import { useContext } from "react";
import { ChevronsUpDown, type LucideIcon } from "lucide-react";
import { ThemeContext, type Theme } from "@hub/shared/hooks/useTheme";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@hub/ui";

function LogoMark() {
  return (
    <svg className="mark" viewBox="0 0 40 40" fill="none" aria-hidden>
      <g strokeWidth="3.4" strokeLinecap="round" fill="none">
        <path d="M5 26 C13 14, 21 30, 35 16" stroke="#90B6FF" />
        <path d="M5 20 C14 30, 22 12, 35 24" stroke="#4981EF" />
        <path d="M5 14 C12 22, 24 10, 35 12" stroke="#021B38" />
      </g>
    </svg>
  );
}

export interface NavAnchor {
  label: string;
  /** Lucide icon — shown beside the label, and the ONLY affordance when the
   *  sidebar is collapsed (so the active highlight has something to sit on). */
  icon?: LucideIcon;
  /** count chip (e.g. queue size), optional */
  count?: number;
  active?: boolean;
  onSelect?: () => void;
}

export function Sidebar({
  collapsed = false,
  pins = [],
  anchors = [],
  user,
}: {
  collapsed?: boolean;
  /** pinned entities/views (personalization) */
  pins?: NavAnchor[];
  /** standing workspace anchors (Home, Connected systems, …) */
  anchors?: NavAnchor[];
  user?: { initials: string; name: string; role: string };
}) {
  return (
    <aside className={collapsed ? "sidebar collapsed" : "sidebar"}>
      <div className="logo-wrap">
        <div className="logo">
          <LogoMark />
          <span>
            coronis<span style={{ color: "var(--ch-blue-600)" }}>·</span>hub
          </span>
        </div>
      </div>

      {pins.length > 0 && (
        <>
          <div className="nav-section">Pinned</div>
          <nav className="nav">
            {pins.map((p) => (
              <NavItem key={p.label} {...p} />
            ))}
          </nav>
        </>
      )}

      {anchors.length > 0 && (
        <>
          <div className="nav-section">Workspace</div>
          <nav className="nav">
            {anchors.map((a) => (
              <NavItem key={a.label} {...a} />
            ))}
          </nav>
        </>
      )}

      {user && <UserMenu user={user} />}
    </aside>
  );
}

/** The three shell themes, in display order (drives the user-area theme control). */
const THEMES: { value: Exclude<Theme, "system">; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "off-white", label: "Off-white" },
  { value: "dark", label: "Dark" },
];

/** The signed-in identity + avatar, shared by the plain foot and the menu trigger. */
function UserFoot({
  user,
  caret,
}: {
  user: { initials: string; name: string; role: string };
  caret?: boolean;
}) {
  return (
    <>
      <span className="avatar">{user.initials}</span>
      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <div style={{ fontSize: 12.5, fontWeight: 600 }}>{user.name}</div>
        <div style={{ fontSize: 11, color: "var(--fg-tertiary)" }}>
          {user.role}
        </div>
      </div>
      {caret && (
        <ChevronsUpDown className="foot-caret size-3.5 shrink-0" aria-hidden />
      )}
    </>
  );
}

/** Sidebar-footer user area (HUB-504): the profile opens a menu holding the
 *  Light / Off-white / Dark theme control (relocated here from the top bar). The
 *  theme mechanism is unchanged — this just moves where the control lives. Outside
 *  a ThemeProvider (e.g. isolated tests) it degrades to the plain, non-interactive
 *  profile so it never throws. */
function UserMenu({
  user,
}: {
  user: { initials: string; name: string; role: string };
}) {
  const theme = useContext(ThemeContext);
  if (!theme) {
    return (
      <div className="side-foot">
        <UserFoot user={user} />
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="side-foot side-foot-btn"
          aria-label="Account and theme"
        >
          <UserFoot user={user} caret />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="top"
        sideOffset={8}
        className="w-[224px]"
      >
        <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
        <div className="px-2 pb-1.5 text-xs text-muted-foreground">
          {user.role}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Theme
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme.resolvedTheme}
          onValueChange={(v) => theme.setTheme(v as Theme)}
        >
          {THEMES.map((t) => (
            <DropdownMenuRadioItem key={t.value} value={t.value}>
              {t.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NavItem({ label, icon: Icon, count, active, onSelect }: NavAnchor) {
  return (
    <button
      type="button"
      className={active ? "nav-item on" : "nav-item"}
      onClick={onSelect}
      // The label becomes a native tooltip — the only way to read a collapsed item.
      title={label}
      aria-label={label}
    >
      {Icon && <Icon className="nav-ico size-[18px] shrink-0" aria-hidden />}
      <span>{label}</span>
      {typeof count === "number" && (
        <span className="pin-v" style={{ fontWeight: 700, fontSize: 11 }}>
          {count}
        </span>
      )}
    </button>
  );
}
